import Debug from '../utils/Debug.js';
import Material from '../materials/Material.js';

/**
 * Lazy K-D Tree for cuboid subdivision to resolve depth cycles.
 *
 * This tree is built incrementally - splits are only added when true
 * geometric cycles are detected during sorting. Cuboids that cross
 * a split plane are subdivided into fragments.
 */
export default class KDTree {
  constructor() {
    // Map from original cuboid key to its fragments
    // Key: parentKey, Value: Array of fragment Cuboids
    this.fragments = new Map();

    // Set of cuboid keys that have been split
    this.splitCuboids = new Set();

    // Global limits to prevent infinite subdivision
    this.MAX_FRAGMENTS_PER_CUBOID = 16;
    this.MAX_TOTAL_FRAGMENTS = 500;

    // Track which cuboids have hit their fragment limit (for deduplication)
    this.exhaustedCuboids = new Set();
    // Track if total fragment limit was logged this session
    this._totalLimitWarned = false;

    // Accumulated split positions per parent cuboid (for invalidate-and-re-split).
    // When persistent fragments need new splits, we merge new positions with these
    // accumulated ones and re-split the original cuboid from scratch, preventing
    // cascading sub-splits that cause fragment count to grow unboundedly.
    // Map: parentKey -> { x: Set<number>, z: Set<number>, y: Set<number> }
    this.accumulatedSplits = new Map();
  }

  /**
   * Check if a cuboid has been split into fragments.
   * @param {string} cuboidKey - The cuboid's key
   * @return {boolean} True if the cuboid has fragments
   */
  hasFragments(cuboidKey) {
    return this.fragments.has(cuboidKey);
  }

  /**
   * Get fragments for a cuboid (if any).
   * @param {string} cuboidKey - The cuboid's key
   * @return {Cuboid[]|null} Array of fragments, or null if not split
   */
  getFragments(cuboidKey) {
    return this.fragments.get(cuboidKey) || null;
  }

  /**
   * Find a fragment by its key.
   * Searches through all fragments to find one matching the given key.
   * @param {string} fragmentKey - The fragment's key (position,dimensions)
   * @return {Cuboid|null} The fragment, or null if not found
   */
  getFragmentByKey(fragmentKey) {
    for (const frags of this.fragments.values()) {
      for (const fragment of frags) {
        if (fragment.getKey() === fragmentKey) {
          return fragment;
        }
      }
    }
    return null;
  }

  /**
   * Get the number of fragments for a cuboid.
   * @param {string} cuboidKey - The cuboid's key (or parentKey for fragments)
   * @return {number} Number of fragments
   */
  getFragmentCount(cuboidKey) {
    const frags = this.fragments.get(cuboidKey);
    return frags ? frags.length : 0;
  }

  /**
   * Get total fragment count across all cuboids.
   * @return {number} Total fragments
   */
  getTotalFragmentCount() {
    let total = 0;
    for (const frags of this.fragments.values()) {
      total += frags.length;
    }
    return total;
  }

  /**
   * Check if we can add more fragments (within limits).
   * @param {string} parentKey - The parent cuboid's key
   * @param {boolean} silent - If true, don't log warnings (for checking without side effects)
   * @return {boolean} True if more fragments can be added
   */
  canAddFragments(parentKey, silent = false) {
    // Quick check for already-exhausted cuboids (avoids counting)
    if (this.exhaustedCuboids.has(parentKey)) {
      return false;
    }

    if (this.getFragmentCount(parentKey) >= this.MAX_FRAGMENTS_PER_CUBOID) {
      // Track this cuboid as exhausted to prevent repeated checks
      this.exhaustedCuboids.add(parentKey);
      // Only warn once per cuboid
      if (!silent) {
        Debug.warn('KDTree', `Max fragments per cuboid reached for ${parentKey}`);
      }
      return false;
    }

    if (this.getTotalFragmentCount() >= this.MAX_TOTAL_FRAGMENTS) {
      // Only warn once per session
      if (!silent && !this._totalLimitWarned) {
        Debug.warn('KDTree', 'Max total fragments reached');
        this._totalLimitWarned = true;
      }
      return false;
    }
    return true;
  }

  /**
   * Check if a cuboid has been exhausted (hit fragment limit).
   * Fast check that doesn't count fragments.
   * @param {string} parentKey - The parent cuboid's key
   * @return {boolean} True if the cuboid cannot accept more fragments
   */
  isExhausted(parentKey) {
    return this.exhaustedCuboids.has(parentKey);
  }

  /**
   * Reset warning tracking (call at start of new sorting operation).
   */
  resetWarningState() {
    this._totalLimitWarned = false;
  }

  /**
   * Merge new split positions into the accumulated set for a parent cuboid.
   * Used by invalidate-and-re-split: positions accumulate across frames so
   * the original cuboid can always be re-split with the full set at once.
   * Deduplicates positions within EPSILON to prevent floating-point drift
   * (e.g., sprite moving by tiny amounts) from inflating the set.
   * @param {string} parentKey - The parent cuboid's key
   * @param {string} axis - 'x', 'z', or 'y'
   * @param {Iterable<number>} positions - Split positions to merge
   * @return {{ entry: Object, changed: boolean }} The accumulated entry and whether new positions were added
   */
  mergeAccumulatedSplits(parentKey, axis, positions) {
    if (!this.accumulatedSplits.has(parentKey)) {
      this.accumulatedSplits.set(parentKey, { x: new Set(), z: new Set(), y: new Set() });
    }
    const entry = this.accumulatedSplits.get(parentKey);
    const axisSet = entry[axis];
    const EPSILON = 0.01;
    let changed = false;
    for (const pos of positions) {
      // Check if any existing position is within EPSILON
      let isDuplicate = false;
      for (const existing of axisSet) {
        if (Math.abs(existing - pos) < EPSILON) {
          isDuplicate = true;
          break;
        }
      }
      if (!isDuplicate) {
        axisSet.add(pos);
        changed = true;
      }
    }
    return { entry, changed };
  }

  /**
   * Add fragments for a cuboid that was split.
   * @param {string} parentKey - The original cuboid's key
   * @param {Cuboid[]} newFragments - The fragments created from subdivision
   * @param {HTMLElement} container - DOM container to add fragment elements to
   */
  addFragments(parentKey, newFragments, container) {
    if (!newFragments || newFragments.length === 0) return;

    // Get or create the fragments array for this parent
    let existingFragments = this.fragments.get(parentKey);

    if (!existingFragments) {
      // First split of this cuboid
      existingFragments = [];
      this.fragments.set(parentKey, existingFragments);
      this.splitCuboids.add(parentKey);
    }

    // Add new fragments
    for (const fragment of newFragments) {
      existingFragments.push(fragment);
      // Add fragment's DOM element to container
      if (container && fragment.element) {
        container.appendChild(fragment.element);
      }
    }
  }

  /**
   * Replace a fragment with its subdivisions (when a fragment itself needs splitting).
   * @param {Cuboid} oldFragment - The fragment being replaced
   * @param {Cuboid[]} newFragments - The new fragments
   * @param {HTMLElement} container - DOM container
   */
  replaceFragment(oldFragment, newFragments, container) {
    const parentKey = oldFragment.parentKey;
    if (!parentKey) return;

    const fragments = this.fragments.get(parentKey);
    if (!fragments) return;

    // Find and remove the old fragment
    const index = fragments.indexOf(oldFragment);
    if (index === -1) return;

    // Clean up video elements before removal to prevent orphaned WebMediaPlayers
    this._cleanupFragmentVideos(oldFragment);

    // Remove old fragment from DOM
    if (oldFragment.element && oldFragment.element.parentNode) {
      oldFragment.element.parentNode.removeChild(oldFragment.element);
    }

    // Remove from array
    fragments.splice(index, 1);

    // Add new fragments
    for (const fragment of newFragments) {
      fragments.push(fragment);
      if (container && fragment.element) {
        container.appendChild(fragment.element);
      }
    }
  }

  /**
   * Invalidate all fragments for a cuboid (e.g., when cuboid moves).
   * @param {string} cuboidKey - The cuboid's key
   */
  invalidateCuboid(cuboidKey) {
    const fragments = this.fragments.get(cuboidKey);
    if (!fragments) return;

    // Clean up and remove fragment DOM elements
    for (const fragment of fragments) {
      this._cleanupFragmentVideos(fragment);
      if (fragment.element && fragment.element.parentNode) {
        fragment.element.parentNode.removeChild(fragment.element);
      }
    }

    // Clear the fragments and reset exhaustion state
    this.fragments.delete(cuboidKey);
    this.splitCuboids.delete(cuboidKey);
    this.exhaustedCuboids.delete(cuboidKey);
  }

  /**
   * Get all renderable items for sorting.
   * For non-split cuboids, returns the cuboid itself.
   * For split cuboids, returns its fragments instead.
   *
   * @param {Map<string, Cuboid>} allCuboids - All cuboids in the world
   * @return {Cuboid[]} Array of cuboids and fragments to render
   */
  getRenderables(allCuboids) {
    const renderables = [];

    for (const [key, cuboid] of allCuboids) {
      // Skip collision-only cuboids (e.g. invisible ceilings)
      if (cuboid._colliderOnly) continue;

      if (this.splitCuboids.has(key)) {
        // This cuboid has been split - use its fragments
        const fragments = this.fragments.get(key);
        if (fragments) {
          renderables.push(...fragments);
        }
      } else {
        // Not split - use original cuboid
        renderables.push(cuboid);
      }
    }

    return renderables;
  }

  /**
   * Hide the original cuboid element when it has been split.
   * @param {Cuboid} cuboid - The cuboid that was split
   */
  hideOriginal(cuboid) {
    if (cuboid.element) {
      cuboid.element.style.visibility = 'hidden';
    }
  }

  /**
   * Show the original cuboid element (when fragments are cleared).
   * @param {Cuboid} cuboid - The cuboid to show
   */
  showOriginal(cuboid) {
    if (cuboid.element) {
      cuboid.element.style.visibility = '';
    }
  }

  /**
   * Clean up video elements on a fragment before removal.
   * Prevents orphaned WebMediaPlayers from accumulating.
   * @param {Cuboid} fragment - The fragment to clean up
   */
  _cleanupFragmentVideos(fragment) {
    if (!fragment.faceElements) return;
    for (const face of Object.keys(fragment.faceElements)) {
      const faceEl = fragment.faceElements[face];
      if (!faceEl) continue;
      const videos = faceEl.querySelectorAll('video');
      videos.forEach(video => {
        Material.cleanupVideo(video);
      });
    }
  }

  /**
   * Clear all fragments and reset the tree.
   */
  clear() {
    // Clean up and remove all fragment DOM elements
    for (const fragments of this.fragments.values()) {
      for (const fragment of fragments) {
        this._cleanupFragmentVideos(fragment);
        if (fragment.element && fragment.element.parentNode) {
          fragment.element.parentNode.removeChild(fragment.element);
        }
      }
    }

    this.fragments.clear();
    this.splitCuboids.clear();
    this.exhaustedCuboids.clear();
    this.accumulatedSplits.clear();
    this._totalLimitWarned = false;
  }

  /**
   * Get statistics about the current tree state.
   * @return {Object} Statistics object
   */
  getStats() {
    return {
      splitCuboids: this.splitCuboids.size,
      totalFragments: this.getTotalFragmentCount(),
      maxPerCuboid: Math.max(0, ...Array.from(this.fragments.values()).map(f => f.length))
    };
  }
}
